import { useState, useRef, useEffect, useCallback } from "react";
import supabase from "../supabase";
import axios from "axios";

/**
 * Extracts an array of trades from the analysis result,
 * which might be a JSON string or an array.
 * @param {object} analysis - The selected analysis object.
 * @returns {string[]} An array of unique trades.
 */
const getTradesFromAnalysis = (analysis) => {
  // L'objectif est de récupérer les métiers directement depuis 'analysis.corps_de_metier'
  // Selon le schéma, 'corps_de_metier' est une propriété directe de l'objet 'analysis'.
  // Il n'y a pas de 'analysis_result' intermédiaire pour cette information.
  
  let corpsDeMetier = analysis?.analysis_result?.corps_de_metier;
  

  // Si 'corpsDeMetier' est une chaîne de caractères (par ex., si elle a été stringifiée)
  if (typeof corpsDeMetier === 'string') {
    try {
      corpsDeMetier = JSON.parse(corpsDeMetier);
      
    } catch (e) {
      console.error("   => Erreur de parsing JSON pour 'corps_de_metier':", e);
      return [];
    }
  }

  // Vérifier si c'est bien un tableau
  if (!Array.isArray(corpsDeMetier)) {
    
    return [];
  }

  

  const trades = corpsDeMetier.reduce((acc, item, index) => {
    
    if (typeof item !== 'object' || item === null) {
      
      return acc;
    }

    const trade = item.nom_metier;
    

    if (trade && typeof trade === 'string' && !acc.includes(trade)) { // Ajout d'une vérification de type pour 'trade'
      acc.push(trade);
      
    } else if (trade) {
      
    } else {
      
    }
    return acc;
  }, []);

  
  return trades;
};


/**
 * Normalizes artisan data from different sources to a consistent format for the UI.
 * @param {object[]} artisans - The array of artisans to normalize.
 * @returns {object[]} The normalized array of artisans.
 */
const normalizeArtisanData = (artisans) => {
  if (!artisans || !Array.isArray(artisans)) {
    return [];
  }

  return artisans.map((artisan, index) => {
    // Check if it's a prospected artisan by looking for a unique field like 'trade'.
    const isProspected = artisan.trade !== undefined;

    return {
      id: artisan.id || `prospect-${index}-${Date.now()}`,
      name: artisan.name || artisan.nom,
      // Map 'trade' (from prospect) or 'metier' (from registered) to 'metier'
      metier: isProspected ? artisan.trade : artisan.metier,
      // Map 'logo_url' (from prospect) or 'logo' (from registered) to 'logo'
      logo: isProspected ? artisan.logo_url : artisan.logo,
      // Map 'website' (from prospect) or 'site' (from registered) to 'site'
      site: isProspected ? artisan.website : (artisan.site_web || artisan.site),
      // Map 'phone' (from prospect) or 'telephone' (from registered) to 'telephone'
      telephone: isProspected ? artisan.phone : artisan.telephone,
      // Map 'city' (from prospect) or 'adresse' (from registered) to 'adresse' for display
      adresse: isProspected ? artisan.city : (artisan.address || artisan.adresse || artisan.localisation),
      email: artisan.email, // Only exists for registered artisans
      rang: artisan.rang,   // Only exists for registered artisans
    };
  });
};

export default function useArtisanDiscovery(room, selectedAnalysis) {
  const [artisans, setArtisans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isProspected, setIsProspected] = useState(false);
  const artisansRef = useRef(null);

  // Reset state when the analysis or room changes
  useEffect(() => {
    setArtisans([]);
    setIsProspected(false);
  }, [selectedAnalysis, room]);

  /**
   * Fetches artisans already registered in the database for a specific room.
   */
  const findRegisteredArtisans = useCallback(async (roomId) => {
    
    const { data, error } = await supabase.rpc('get_artisans_for_room', { p_room_id: roomId });

    if (error) {
      console.error("Erreur RPC (get_artisans_for_room):", error.message);
      throw new Error("Impossible de chercher les artisans inscrits.");
    }

    
    return data || [];
  }, []);

  /**
   * Fetches new, unregistered artisans using the AI backend.
   */
  const findUnregisteredArtisans = useCallback(async (analysis, location) => {
    
    const trades = getTradesFromAnalysis(analysis);

    if (trades.length === 0) {
      
      return [];
    }
    if (!location) {
      console.warn("   => Localisation non fournie pour la recherche IA.");
    }

    try {
      
      const response = await axios.post(`${process.env.REACT_APP_SERVER_URL}/openai/find-unregistered-artisans`, { 
        trades,
        localisation_projet: location 
      });
      const prospectedArtisans = response.data.artisans || [];
      
      
      return prospectedArtisans;
    } catch (apiError) {
      console.error("Erreur API (find-unregistered-artisans):", apiError.response?.data || apiError.message);
      throw new Error("La recherche de nouveaux artisans a échoué.");
    }
  }, []);

  /**
   * Main handler to orchestrate the artisan discovery process.
   */
  const handleShowArtisans = useCallback(async () => {
    
    if (!room?.id || !selectedAnalysis?.id) {
      
      return;
    }

    setLoading(true);
    setArtisans([]);
    setIsProspected(false);

    try {
      const registeredArtisans = await findRegisteredArtisans(room.id);

      if (registeredArtisans.length > 0) {
        setArtisans(normalizeArtisanData(registeredArtisans));
      } else {
        const location = room.property_localisation;
        const prospectedArtisans = await findUnregisteredArtisans(selectedAnalysis, location);
        setArtisans(normalizeArtisanData(prospectedArtisans));
        setIsProspected(true);
      }
    } catch (error) {
      console.error(`❌ Erreur finale dans le processus de découverte: ${error.message}`);
      setArtisans([]); // Ensure state is clean on failure
    } finally {
      setLoading(false);
      
      setTimeout(() => {
        artisansRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [room, selectedAnalysis, findRegisteredArtisans, findUnregisteredArtisans]);

  return {
    artisans,
    loadingArtisans: loading,
    isProspected,
    artisansRef,
    handleShowArtisans,
  };
}
